/**
 * CompanyPaymentEntitiesPage — manages "قائمة المدفوعات للشركة" (payment entities).
 * Each entity: name AR/EN (auto-translate), optional foreign currency + exchange
 * rate to AED. Consumed by the Company Payments add-invoice dialog.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit3, Trash2, Check, X, Loader2, Briefcase, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { DirhamPrice } from '../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FOREIGN_CURRENCIES = [
  { code: 'USD', ar: 'دولار أمريكي', symbol: '$' },
  { code: 'EUR', ar: 'يورو', symbol: '€' },
  { code: 'GBP', ar: 'جنيه إسترليني', symbol: '£' },
  { code: 'SAR', ar: 'ريال سعودي', symbol: 'ر.س' },
  { code: 'KWD', ar: 'دينار كويتي', symbol: 'د.ك' },
  { code: 'QAR', ar: 'ريال قطري', symbol: 'ر.ق' },
  { code: 'BHD', ar: 'دينار بحريني', symbol: 'د.ب' },
  { code: 'OMR', ar: 'ريال عماني', symbol: 'ر.ع' },
  { code: 'JPY', ar: 'ين ياباني', symbol: '¥' },
];

const EMPTY_FORM = {
  name_ar: '', name_en: '', foreign: false, currency: '', exchange_rate: '',
  // iter-218g (Feb 2026) — Mandatory entity type from the shared
  // dropdown_entity_types list (16 seeded categories).
  entity_type_id: '',
};

// iter-217c-v8: Language input guards + explicit translate buttons.
// Arabic field: allow Arabic letters + digits + spaces + common punctuation only.
// English field: allow Latin letters + digits + spaces + common punctuation only.
const _AR_ALLOWED_RX = /[^\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF0-9\s.,\-&()\/'"]/g;
const _EN_ALLOWED_RX = /[^A-Za-z0-9\s.,\-&()\/'"]/g;
const filterAr = (s) => (s || '').replace(_AR_ALLOWED_RX, '');
const filterEn = (s) => (s || '').replace(_EN_ALLOWED_RX, '');

function EntityForm({ form, setForm, language, translatingField, autoTranslate, entityTypes, entityTypesLoading }) {
  const isAr = language === 'ar';
  const canTranslateArToEn = form.name_ar.trim().length > 0 && translatingField === null;
  const canTranslateEnToAr = form.name_en.trim().length > 0 && translatingField === null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-[#A1A1AA] mb-2 flex items-center justify-between gap-2">
            <span>{isAr ? 'الاسم بالعربية' : 'Name in Arabic'} *</span>
            <div className="flex items-center gap-2">
              {translatingField === 'ar' && (
                <span className="flex items-center gap-1 text-[10px] text-cyan-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {isAr ? 'جارٍ الترجمة...' : 'Translating...'}
                </span>
              )}
              <button
                type="button"
                disabled={!canTranslateEnToAr}
                onClick={() => autoTranslate({ text: form.name_en, source: 'en', target: 'ar', fieldKey: 'ar', applyFn: (t) => setForm((p) => ({ ...p, name_ar: filterAr(t) })) })}
                className="text-[10px] px-2 py-0.5 rounded border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                title={isAr ? 'ترجم من الإنجليزية' : 'Translate from English'}
                data-testid="translate-en-to-ar-btn"
              >
                {isAr ? '← ترجم من EN' : '← From EN'}
              </button>
            </div>
          </label>
          <Input
            value={form.name_ar}
            onChange={(e) => setForm({ ...form, name_ar: filterAr(e.target.value) })}
            onBlur={() => {
              if (form.name_ar.trim() && !form.name_en.trim()) {
                autoTranslate({ text: form.name_ar, source: 'ar', target: 'en', fieldKey: 'en', applyFn: (t) => setForm((p) => ({ ...p, name_en: filterEn(t) })) });
              }
            }}
            placeholder={isAr ? 'مثال: جوجل وورك سبيس' : 'مثال: جوجل وورك سبيس'}
            className="bg-white/5 border-white/10 text-white"
            dir="rtl"
            lang="ar"
            data-testid="entity-name-ar"
          />
        </div>
        <div>
          <label className="block text-sm text-[#A1A1AA] mb-2 flex items-center justify-between gap-2">
            <span>{isAr ? 'الاسم بالإنجليزية' : 'Name in English'} *</span>
            <div className="flex items-center gap-2">
              {translatingField === 'en' && (
                <span className="flex items-center gap-1 text-[10px] text-cyan-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {isAr ? 'جارٍ الترجمة...' : 'Translating...'}
                </span>
              )}
              <button
                type="button"
                disabled={!canTranslateArToEn}
                onClick={() => autoTranslate({ text: form.name_ar, source: 'ar', target: 'en', fieldKey: 'en', applyFn: (t) => setForm((p) => ({ ...p, name_en: filterEn(t) })) })}
                className="text-[10px] px-2 py-0.5 rounded border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                title={isAr ? 'ترجم من العربية' : 'Translate from Arabic'}
                data-testid="translate-ar-to-en-btn"
              >
                {isAr ? 'ترجم من AR →' : 'From AR →'}
              </button>
            </div>
          </label>
          <Input
            value={form.name_en}
            onChange={(e) => setForm({ ...form, name_en: filterEn(e.target.value) })}
            onBlur={() => {
              if (form.name_en.trim() && !form.name_ar.trim()) {
                autoTranslate({ text: form.name_en, source: 'en', target: 'ar', fieldKey: 'ar', applyFn: (t) => setForm((p) => ({ ...p, name_ar: filterAr(t) })) });
              }
            }}
            placeholder="Example: Google Workspace"
            className="bg-white/5 border-white/10 text-white"
            dir="ltr"
            lang="en"
            data-testid="entity-name-en"
          />
        </div>
      </div>
      <p className="text-xs text-[#606060]">
        {isAr
          ? '💡 اكتب الاسم بإحدى اللغتين ثم اضغط زر الترجمة، أو اترك الحقل الآخر فارغاً ليُترجم تلقائياً. الحقل العربي يقبل عربي فقط، والإنجليزي يقبل إنجليزي فقط.'
          : '💡 Type the name in either language then click the translate button, or leave the other field empty for auto-translate. Arabic field accepts Arabic only, English accepts English only.'}
      </p>

      {/* iter-218g (Feb 2026) — Mandatory entity type dropdown, fed by
          /api/dropdowns/entity-types (16 seeded categories). */}
      <div>
        <label className="block text-sm text-[#A1A1AA] mb-2">
          {isAr ? 'نوع الجهة' : 'Entity type'} <span className="text-red-400">*</span>
        </label>
        <select
          value={form.entity_type_id || ''}
          onChange={(e) => setForm({ ...form, entity_type_id: e.target.value })}
          className="w-full bg-[#1a1a2e] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500"
          style={{ colorScheme: 'dark' }}
          data-testid="entity-type-select"
        >
          <option value="" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
            {entityTypesLoading
              ? (isAr ? 'جارٍ التحميل…' : 'Loading…')
              : (isAr ? '— اختر نوع الجهة —' : '— Select entity type —')}
          </option>
          {entityTypes.map((et) => (
            <option key={et.id} value={et.id} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
              {isAr ? et.name_ar : (et.name_en || et.name_ar)}
            </option>
          ))}
        </select>
        {!entityTypesLoading && entityTypes.length === 0 && (
          <p className="text-[11px] text-amber-300 mt-1">
            {isAr
              ? 'لا توجد أنواع بعد — أضفها من: القوائم المنسدلة ▸ أنواع الجهات'
              : 'No entity types yet — add them from: Dropdown Lists ▸ Entity Types'}
          </p>
        )}
      </div>

      {/* Foreign currency question */}
      <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
        <p className="text-sm text-white mb-3">
          {isAr ? 'هل دفعات هذه الجهة بعملة غير الدرهم؟' : 'Does this entity charge in a currency other than AED?'}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setForm({ ...form, foreign: false, currency: '', exchange_rate: '' })}
            className={`px-4 py-2 rounded-lg border text-sm font-bold transition ${!form.foreign ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300' : 'bg-white/5 border-white/10 text-white/50 hover:text-white'}`}
            data-testid="entity-currency-no"
          >
            {isAr ? 'لا — بالدرهم' : 'No — AED'}
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, foreign: true })}
            className={`px-4 py-2 rounded-lg border text-sm font-bold transition ${form.foreign ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-white/5 border-white/10 text-white/50 hover:text-white'}`}
            data-testid="entity-currency-yes"
          >
            {isAr ? 'نعم — عملة أخرى' : 'Yes — other currency'}
          </button>
        </div>

        {form.foreign && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">{isAr ? 'اختر العملة' : 'Select currency'} *</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                style={{ colorScheme: 'dark' }}
                data-testid="entity-currency-select"
              >
                <option value="">{isAr ? 'اختر العملة' : 'Select currency'}</option>
                {FOREIGN_CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.ar}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">
                {isAr ? `سعر الصرف (1 ${form.currency || '—'} = ? درهم)` : `Exchange rate (1 ${form.currency || '—'} = ? AED)`} *
              </label>
              <Input
                type="number"
                step="0.0001"
                value={form.exchange_rate}
                onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })}
                placeholder="3.6725"
                className="bg-white/5 border-white/10 text-white"
                dir="ltr"
                data-testid="entity-exchange-rate"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CompanyPaymentEntitiesPage() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAr = language === 'ar';
  const headers = { Authorization: `Bearer ${token}` };

  // iter-211 — this page is reachable from BOTH:
  //   /control-panel-settings/dropdowns/company-payment-entities   (legacy path)
  //   /dashboard/finance/settings/payment-entities                 (new finance-scoped)
  // The finance card lives under Finance → Settings → Dropdown Lists, so the breadcrumb
  // and back button follow that exact chain.
  const fromFinance = location.pathname.startsWith('/dashboard/finance');
  const backPath = fromFinance ? '/dashboard/finance/settings/dropdown-lists' : '/control-panel-settings/dropdowns';
  const breadcrumb = fromFinance
    ? [
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'إعدادات القسم المالي', labelEn: 'Finance Settings', path: '/dashboard/finance/settings' },
        { labelAr: 'القوائم المنسدلة', labelEn: 'Dropdown Lists', path: '/dashboard/finance/settings/dropdown-lists' },
        { labelAr: 'جهات الدفع وأسعار الصرف', labelEn: 'Payment Entities & Rates' },
      ]
    : [
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'القوائم المنسدلة', labelEn: 'Dropdown Lists', path: '/control-panel-settings/dropdowns' },
        { labelAr: 'قائمة المدفوعات للشركة', labelEn: 'Company Payments List' },
      ];

  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null); // entity being edited
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [translatingField, setTranslatingField] = useState(null);
  // iter-218g (Feb 2026) — Entity types dropdown feed.
  const [entityTypes, setEntityTypes] = useState([]);
  const [entityTypesLoading, setEntityTypesLoading] = useState(true);

  const autoTranslate = async ({ text, source, target, fieldKey, applyFn }) => {
    if (!text || !text.trim()) return;
    setTranslatingField(fieldKey);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/utils/translate`, { text: text.trim(), source, target }, { headers });
      const translated = (res.data?.translated || '').trim();
      if (translated) applyFn(translated);
    } catch {
      toast.error(isAr ? 'فشل في الترجمة التلقائية' : 'Auto-translate failed');
    } finally {
      setTranslatingField(null);
    }
  };

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/finance/payment-services`, { headers });
      setEntities(res.data.data || []);
    } catch {
      toast.error(isAr ? 'فشل في جلب الجهات' : 'Failed to fetch entities');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  // iter-218g (Feb 2026) — Preload entity-types list once on mount.
  useEffect(() => {
    (async () => {
      setEntityTypesLoading(true);
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/dropdowns/entity-types`,
          { headers, params: { active_only: true } },
        );
        setEntityTypes(res.data?.data || []);
      } catch {
        /* silent — the form shows the empty-state hint */
      } finally {
        setEntityTypesLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = (f) => {
    if (!f.name_ar.trim() || !f.name_en.trim()) {
      toast.error(isAr ? 'يرجى إدخال الاسم بالعربية والإنجليزية' : 'Enter both Arabic & English names');
      return false;
    }
    // iter-218g — Entity type is required.
    if (!f.entity_type_id) {
      toast.error(isAr ? 'اختر نوع الجهة' : 'Select an entity type');
      return false;
    }
    if (f.foreign && (!f.currency || !f.exchange_rate || Number(f.exchange_rate) <= 0)) {
      toast.error(isAr ? 'اختر العملة وحدد سعر الصرف' : 'Select currency and set the exchange rate');
      return false;
    }
    return true;
  };

  const buildPayload = (f) => {
    // Snapshot AR + EN entity-type labels from the current dropdown feed so
    // reports and historical rows stay stable if the dropdown is later
    // renamed.
    const picked = entityTypes.find((et) => et.id === f.entity_type_id) || {};
    return {
      name: f.name_ar.trim(),
      name_ar: f.name_ar.trim(),
      name_en: f.name_en.trim(),
      currency: f.foreign ? f.currency : 'AED',
      exchange_rate: f.foreign ? Number(f.exchange_rate) : null,
      entity_type_id: f.entity_type_id,
      entity_type_ar: picked.name_ar || null,
      entity_type_en: picked.name_en || null,
    };
  };

  const handleAdd = async () => {
    if (!validate(form)) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/finance/payment-services`, buildPayload(form), { headers });
      toast.success(isAr ? 'تمت إضافة الجهة بنجاح' : 'Entity added');
      setForm(EMPTY_FORM);
      fetchEntities();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!validate(editForm)) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/finance/payment-services/${editing.id}`, buildPayload(editForm), { headers });
      toast.success(isAr ? 'تم تحديث الجهة' : 'Entity updated');
      setEditing(null);
      fetchEntities();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entity) => {
    const label = isAr ? (entity.name_ar || entity.name) : (entity.name_en || entity.name);
    // iter-218m (Feb 2026) — Per user: remove the "default entity" concept.
    // All entities are treated equally now — no special badge, no scarier
    // delete confirmation for one over another.
    const warn = (isAr ? `حذف "${label}"؟` : `Delete "${label}"?`);
    if (!window.confirm(warn)) return;
    setDeletingId(entity.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/finance/payment-services/${entity.id}`, { headers });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      fetchEntities();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (entity) => {
    setEditing(entity);
    setEditForm({
      name_ar: entity.name_ar || entity.name || '',
      name_en: entity.name_en || entity.name || '',
      foreign: !!(entity.currency && entity.currency !== 'AED'),
      currency: entity.currency && entity.currency !== 'AED' ? entity.currency : '',
      exchange_rate: entity.exchange_rate != null ? String(entity.exchange_rate) : '',
      entity_type_id: entity.entity_type_id || '',
    });
  };

  return (
    <DashboardLayout
      title="قائمة المدفوعات للشركة"
      titleEn="Company Payments List"
      subtitle="جهات الدفع: الاسم، العملة، وسعر الصرف"
      subtitleEn="Payment entities: name, currency and exchange rate"
      screenCode="DDL-001"
      backPath={backPath}
      onBack={() => navigate(backPath)}
      breadcrumb={breadcrumb}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="company-payment-entities-page">
        {/* Add new entity */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <Plus className="h-5 w-5 text-emerald-400" />
            {isAr ? 'إضافة جهة جديدة' : 'Add New Entity'}
          </h3>
          <EntityForm form={form} setForm={setForm} language={language} translatingField={translatingField} autoTranslate={autoTranslate} entityTypes={entityTypes} entityTypesLoading={entityTypesLoading} />
          <Button
            onClick={handleAdd}
            disabled={saving}
            className="mt-4 bg-emerald-600 hover:bg-emerald-700"
            data-testid="entity-add-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : <Plus className="h-4 w-4 ml-2" />}
            {isAr ? 'إضافة' : 'Add'}
          </Button>
        </div>

        {/* Entities list */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <List className="h-5 w-5 text-[#d4af37]" />
            {isAr ? 'الجهات الحالية' : 'Current Entities'}
            <span className="text-sm text-[#71717A] font-normal">({entities.length})</span>
          </h3>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#d4af37]" />
            </div>
          ) : entities.length === 0 ? (
            <p className="text-center text-[#71717A] py-8">{isAr ? 'لا توجد جهات' : 'No entities found'}</p>
          ) : (
            <div className="space-y-3">
              {entities.map((entity) => {
                const foreign = entity.currency && entity.currency !== 'AED';
                return (
                  <motion.div
                    key={entity.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between gap-3 p-4 bg-white/[0.02] rounded-xl border border-white/5"
                    data-testid={`entity-row-${entity.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-blue-500/10 border border-blue-500/30">
                        <Briefcase className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{entity.name_ar || entity.name}</p>
                        <p className="text-[#71717A] text-sm truncate">{entity.name_en || entity.name}</p>
                        {/* iter-218g — Entity-type pill (falls back to the
                            snapshot on the doc, then re-resolves via id
                            for older rows that didn't snapshot the label). */}
                        {(() => {
                          const snapshotAr = entity.entity_type_ar;
                          const snapshotEn = entity.entity_type_en;
                          const resolved   = entityTypes.find((et) => et.id === entity.entity_type_id);
                          const labelAr = snapshotAr || resolved?.name_ar;
                          const labelEn = snapshotEn || resolved?.name_en;
                          if (!labelAr && !labelEn) return null;
                          return (
                            <span
                              className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-[11px] font-semibold"
                              data-testid={`entity-type-badge-${entity.id}`}
                            >
                              {isAr ? (labelAr || labelEn) : (labelEn || labelAr)}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {foreign ? (
                        <span className="px-2.5 py-1 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-bold" dir="ltr">
                          1 {entity.currency} = <DirhamPrice value={entity.exchange_rate} decimals={2} size="sm" />
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold">
                          AED
                        </span>
                      )}
                      {/* iter-218m — "افتراضي/Default" badge removed per user;
                          no entity is treated as default anymore. */}
                      <Button size="sm" variant="ghost" onClick={() => startEdit(entity)} className="text-[#71717A] hover:text-[#d4af37]" data-testid={`entity-edit-${entity.id}`}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(entity)}
                        disabled={deletingId === entity.id}
                        className="text-[#71717A] hover:text-red-400"
                        data-testid={`entity-delete-${entity.id}`}
                      >
                        {deletingId === entity.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1a1a2e] rounded-2xl border border-white/10 w-full max-w-lg"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Edit3 className="h-5 w-5 text-[#d4af37]" />
                  {isAr ? 'تعديل الجهة' : 'Edit Entity'}
                </h2>
                <button onClick={() => setEditing(null)} className="text-[#808080] hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <EntityForm form={editForm} setForm={setEditForm} language={language} translatingField={translatingField} autoTranslate={autoTranslate} entityTypes={entityTypes} entityTypesLoading={entityTypesLoading} />
              <div className="flex gap-3 mt-6">
                <Button onClick={() => setEditing(null)} variant="outline" className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-xl">
                  {isAr ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button onClick={handleUpdate} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="entity-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 ml-2" />}
                  {isAr ? 'حفظ' : 'Save'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}
